import React from 'react';
import { Link } from 'react-router-dom';

/**
 * D-060 Brand Lock — Single Source of Truth for the GIWAHS mark.
 * -----------------------------------------------------------------
 * All logo, icon, wordmark and OG references in the codebase MUST import
 * from this file. The underlying images live in /public/brand/ and are
 * derived directly from the founder-approved source artwork at
 * /public/brand/giwahs-logo-master.png. They are never recoloured,
 * recomposed or regenerated.
 *
 * Catalogue (see /memory/founder_vault/41_BRAND_ASSETS_INVENTORY.md):
 *   - /brand/giwahs-logo-master.png   canonical source (do not modify)
 *   - /brand/giwahs-logo.png          icon + wordmark lockup (975×750)
 *   - /brand/giwahs-icon.png          shield+globe only (630×630)
 *   - /brand/giwahs-icon-512.png      square icon (512×512)
 *   - /brand/giwahs-wordmark.png      GIWAHS wordmark band (895×195)
 *   - /brand/giwahs-brand-poster.png  full presentation poster
 *   - /brand/giwahs-social-og.png     Open-Graph 1200×630
 *   - /brand/favicon-*.png            16/32/48/64
 *   - /brand/apple-touch-icon.png     180×180
 *   - /brand/android-chrome-*.png     192 / 512
 *   - /brand/favicon.ico              multi-resolution
 */

export const BRAND_ASSET_PATHS = {
  // D-085 — Final Founder logo lock · founder-attached cleaned lockup is the canonical
  // source · single asset routed to every surface: navbar, footer, mobile drawer,
  // founder/trust/intelligence pages, empty states, OG metadata, email templates.
  master:    '/brand/giwahs-logo-v4-master.png?v=5',
  lockup:    '/brand/giwahs-logo-v4-master.png?v=5',
  icon:      '/brand/giwahs-shield-v4.png?v=5',
  icon512:   '/brand/giwahs-logo-v4-master.png?v=5',
  wordmark:  '/brand/giwahs-logo-v4-master.png?v=5',
  poster:    '/brand/giwahs-logo-v4-master.png?v=5',
  og:        '/brand/giwahs-logo-v4-master.png?v=5',
};

/**
 * BrandLogo — the icon + wordmark lockup, sized via height.
 * Use everywhere a full GIWAHS logo is required (navbar, footer, login,
 * email previews, legal headers, marketing heroes).
 *
 * @param {number} height Pixel height of the rendered image (default 36).
 * @param {boolean} link  If true (default) wraps in a `<Link to="/">`.
 * @param {string} className Extra Tailwind classes for the wrapper.
 * @param {string} variant 'lockup' (icon+wordmark) | 'icon' | 'wordmark'
 */
export const BrandLogo = ({
  height = 36,
  link = true,
  className = '',
  variant = 'lockup',
  alt = 'GIWAHS — trading as Whaitiri Black Limited',
  testId = 'brand-logo',
  ...rest
}) => {
  const src =
    variant === 'icon' ? BRAND_ASSET_PATHS.icon :
    variant === 'wordmark' ? BRAND_ASSET_PATHS.wordmark :
    BRAND_ASSET_PATHS.lockup;

  const img = (
    <img
      src={src}
      alt={alt}
      data-testid={testId}
      style={{ height: `${height}px`, width: 'auto', display: 'block' }}
      className={className}
      {...rest}
    />
  );

  if (!link) return img;
  return (
    <Link to="/" data-testid="brand-logo-link" aria-label="GIWAHS — home" className="inline-flex">
      {img}
    </Link>
  );
};

/**
 * BrandIcon — shield+globe glyph only. Square. Use for compact spots
 * (favicons, app badges, supplier tier marks, mobile chrome shortcuts).
 */
export const BrandIcon = ({
  size = 32,
  className = '',
  alt = 'GIWAHS mark',
  testId = 'brand-icon',
  ...rest
}) => (
  <img
    src={BRAND_ASSET_PATHS.icon}
    alt={alt}
    data-testid={testId}
    width={size}
    height={size}
    style={{ width: `${size}px`, height: `${size}px`, display: 'block' }}
    className={className}
    {...rest}
  />
);

export default BrandLogo;
